<script setup lang="ts">
import { computed } from 'vue';
import { UiText, UiVerticalDivider } from '@shopexperts/shared';

const props = defineProps<{
  projectValue?: string | null;
  reviewSource: string;
  importedFrom?: string | null;
  appsAndThemes?: Array<{
    name: string;
    icon_url?: string | null;
    type: 'app' | 'theme';
  }>;
}>();

const importedFromLabel = computed(() => {
  if (typeof props.importedFrom !== 'string') return null;
  const trimmed = props.importedFrom.trim();
  return trimmed.length > 0 ? trimmed : null;
});

const reviewSourceText = computed(() => {
  if (importedFromLabel.value) {
    return `Imported from ${importedFromLabel.value}`;
  }
  return props.reviewSource || 'Unknown';
});
</script>

<template>
  <div>
    <!-- Metadata Grid -->
    <div class="flex flex-col md:flex-row items-stretch md:items-center justify-start gap-2 md:gap-6 py-4 mb-4">
      <div class="flex flex-row md:flex-col items-start md:items-start justify-between md:justify-start gap-2 md:gap-0">
        <UiText as="p" variant="h6" class="block" text="Project Value" />
        <UiText as="p" variant="h6" class="font-semibold" :text="props.projectValue || 'Not specified'" />
      </div>
      <UiVerticalDivider thickness="normal" height="h-10" class="hidden md:block" />
      <div class="flex flex-row md:flex-col items-center md:items-start justify-between md:justify-start gap-2 md:gap-0">
        <UiText as="p" variant="h6" class="block" text="Review Source" />
        <UiText
          as="p"
          variant="h6"
          class="font-semibold"
          :class="{ capitalize: !importedFromLabel }"
          :text="reviewSourceText"
        />
      </div>
    </div>

    <!-- Apps and Themes -->
    <div v-if="props.appsAndThemes && props.appsAndThemes.length > 0" class="mb-5">
      <div class="text-sm font-normal text-gray-600 mb-3">Apps and themes used in this project</div>
      <div class="flex flex-wrap gap-3">
        <div
          v-for="item in props.appsAndThemes"
          :key="item.name"
          class="inline-flex items-center gap-2 px-3 py-2 rounded border border-gray-200 bg-white"
        >
          <div class="w-8 h-8 flex-shrink-0 bg-black rounded flex items-center justify-center">
            <img
              v-if="item.icon_url"
              :src="item.icon_url"
              :alt="item.name"
              class="w-6 h-6 object-contain"
            />
            <span v-else class="text-white text-xs">{{ item.type === 'app' ? 'A' : 'T' }}</span>
          </div>
          <span class="text-sm font-medium text-gray-900">{{ item.name }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

